import { useEffect, useState } from "react";
import { Box, Button, LinearProgress, linearProgressClasses, Paper, styled } from "@mui/material";
import LoanSidebar from "../components/review/LoanSidebar";
import { useDealContext, Loan, LoanKey, RetrievedLoanInformation } from "@/contexts/DealContext";
import { useParams, useLocation } from "react-router";
import LoanSetupStep from "@/components/review/LoanSetupStep";
import LoanValidationStep from "@/components/review/LoanValidationStep";
import { getLoanKeysToDisplay, getLoanStatus } from "@/utils/loanStatusUtils";
import QuestionField from "@/components/review/QuestionField";

const ReviewPage = () => {
  const { loanId, keyName } = useParams();
  const location = useLocation();
  const {
    deal: { loans },
    submitFunction,
    isStepValid,
    setIsStepValid,
    answerQuestion,
  } = useDealContext();
  const [questions, setQuestions] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const canGoBack = keyName !== "setup" && keyName != undefined;
  const handlePrevious = () => {
    window.history.back();
  };
  const getLoan = (id: string) => loans.find((loan) => loan.id === id) ?? loans[0];

  const [selectedLoan, setSelectedLoan] = useState<Loan>(loanId ? getLoan(loanId) : loans[0]);

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderStartStartRadius: 2,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[200],
      ...theme.applyStyles("dark", {
        backgroundColor: theme.palette.grey[800],
      }),
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: "#1a90ff",
      ...theme.applyStyles("dark", {
        backgroundColor: "#308fe8",
      }),
    },
  }));

  useEffect(() => {
    if (loanId) setSelectedLoan(getLoan(loanId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId, loans]);

  useEffect(() => {
    let hasValue = true;
    let hasQuestion = false;
    if (!keyName) {
      hasQuestion = false;
      setQuestions([]);
    }
    if (selectedLoan && keyName) {
      const loanKey = selectedLoan[keyName as keyof Loan];
      const isRetrievedLoanInformation =
        typeof loanKey === "object" && loanKey !== null && "value" in (loanKey as any);
      if (isRetrievedLoanInformation) {
        if (!(loanKey as RetrievedLoanInformation).value) hasValue = false;
        const questions = [(loanKey as RetrievedLoanInformation).question].filter(
          (q): q is string => q !== undefined && q !== null && q.trim() !== ""
        );
        setQuestions(questions);
        hasQuestion = questions.length > 0;
      }
      if (!isRetrievedLoanInformation) {
        const keyQuestions: string[] = [];
        selectedLoan[keyName as keyof Loan] &&
          Object.keys(selectedLoan[keyName as keyof Loan] as any).forEach((key) => {
            const fieldData = (selectedLoan[keyName as keyof Loan] as Record<string, any>)[key];
            if (fieldData && typeof fieldData === "object" && !("value" in fieldData)) {
              hasValue = false;
            }
            if (fieldData && typeof fieldData === "object" && "question" in fieldData) {
              keyQuestions.push(fieldData.question);
            }
          });
        const nonEmptyQuestions = keyQuestions.filter(
          (q): q is string => q !== undefined && q !== null && q.trim() !== ""
        );
        setQuestions(nonEmptyQuestions);
        hasQuestion = nonEmptyQuestions.length > 0;
      }
    }
    if (!hasValue || hasQuestion) {
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
  }, [keyName, location]);

  useEffect(() => {
    const loanNumber = loans.length;
    const totalSteps = loans.reduce((acc, loan) => {
      const loanKeys = getLoanKeysToDisplay(loan);
      const steps = loanKeys.length;
      return acc + steps;
    }, 0);
    let completeSteps = 0;
    loans.forEach((loan) => {
      if (loan.type) {
        completeSteps++;
      }
      getLoanKeysToDisplay(loan).forEach((key) => {
        const { status } = getLoanStatus(loan[key]);
        if (status === "Validated") {
          completeSteps++;
        }
      });
    });
    setProgress((completeSteps / totalSteps) * 100);
  }, [selectedLoan, loans]);

  return (
    <Box display="flex">
      {/* Sidebar with scroll */}
      <Box
        overflow="auto"
        width="320px"
        flexShrink={0} // prevent shrinking
      >
        <LoanSidebar />
      </Box>

      {/* Main content */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        minWidth={0} // prevent content from overflowing sidebar
      >
        {/* Progress bar
        <BorderLinearProgress variant="determinate" value={progress} /> */}

        {/* Main content area */}
        {selectedLoan && (
          <Box
            flex={1}
            p={3}
            component={Paper}
            elevation={0}
            overflow="auto"
            style={{ borderRadius: 0 }}
          >
            {keyName && getLoanKeysToDisplay(selectedLoan).includes(keyName as LoanKey) ? (
              <LoanValidationStep keyName={keyName as LoanKey} loan={selectedLoan} />
            ) : (
              <LoanSetupStep loan={selectedLoan} />
            )}

            { (keyName !== "guarantors" && questions.filter((q) => q !== undefined && q !== null && q.trim() !== "" && q !== "null").length > 0) && (
              <QuestionField
                type={keyName === "maturity_date" ? "date" : "text"}
                question={questions[0]}
                onSend={(answer) => {
                  setQuestions((prev) => prev.slice(1));
                  answerQuestion(selectedLoan.id, questions[0], keyName as string, answer);
                }}
              />
            )}
          </Box>
        )}

        {/* Sticky footer */}
        <Box
          display="flex"
          justifyContent="space-between"
          p={3}
          position="sticky"
          bottom={0}
          bgcolor="white"
        >
          <Button disabled={!canGoBack} onClick={handlePrevious}>
            Back
          </Button>
          <div style={{ gap: "10px", display: "flex" }}>
            <Button
              disabled={!isStepValid}
              variant="contained"
              color="primary"
              onClick={submitFunction}
            >
              Next
            </Button>
            {!isStepValid && (
              <Button variant="contained" color="warning" onClick={submitFunction}>
                Skip
              </Button>
            )}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default ReviewPage;
